import { useEffect, useCallback, useRef } from "react";
import LoginWithButton from "@/components/common/LoginWithButton";

interface MicrosoftLoginButtonProps {
  clientId: string;
  tenantId?: string;
  redirectUri: string;
  callback: (response: { code?: string }) => void;
  buttonText: string;
  "aria-label"?: string;
}

export default function MicrosoftLoginButton({
  clientId,
  tenantId = "organizations",
  redirectUri,
  callback,
  buttonText,
  "aria-label": ariaLabel,
}: MicrosoftLoginButtonProps) {
  const processedRef = useRef(false);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (processedRef.current) return;
      if (event.data?.type === "microsoft-auth-callback" && event.data?.code) {
        processedRef.current = true;
        callback({ code: event.data.code });
      }
    },
    [callback],
  );

  useEffect(() => {
    processedRef.current = false;
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handleMessage]);

  const handleClick = () => {
    const params = new URLSearchParams({
      client_id: clientId,
      response_type: "code",
      redirect_uri: redirectUri,
      scope: "openid profile email User.Read",
      response_mode: "query",
      prompt: "select_account",
    });

    const authorizeUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?${params.toString()}`;

    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    window.open(
      authorizeUrl,
      "microsoft-login",
      `width=${width},height=${height},left=${left},top=${top}`,
    );
  };

  return (
    <LoginWithButton
      iconSrc="/images/MicrosoftIcon.svg"
      onClick={handleClick}
      aria-label={ariaLabel ?? "Continue with Microsoft"}
    >
      {buttonText}
    </LoginWithButton>
  );
}
